import React from 'react';
import { Box, IconButton, TextField } from '@mui/material';
import {
  Check as SubmitIcon,
  Close as CancelIcon,
  Edit as EditIcon,
  Delete as RemoveIcon,
} from '@mui/icons-material';
import { sanitizeUrl } from '../utils/url';

interface FloatingLinkEditorModalProps {
  editorRef: React.RefObject<HTMLDivElement>;
  isLink: boolean;
  isLinkEditMode: boolean;
  linkUrl: string;
  linkText: string;
  editedLinkUrl: string;
  editedLinkText: string;
  urlInputRef: React.RefObject<HTMLInputElement>;
  textInputRef: React.RefObject<HTMLInputElement>;
  onEditedLinkUrlChange: (value: string) => void;
  onEditedLinkTextChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

export function FloatingLinkEditorModal({
  editorRef,
  isLink,
  isLinkEditMode,
  linkUrl,
  linkText,
  editedLinkUrl,
  editedLinkText,
  urlInputRef,
  textInputRef,
  onEditedLinkUrlChange,
  onEditedLinkTextChange,
  onSubmit,
  onCancel,
  onEdit,
  onRemove,
}: FloatingLinkEditorModalProps): JSX.Element {
  return (
    <Box
      ref={editorRef}
      sx={{
        display: 'flex',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        maxWidth: 600,
        width: '100%',
        opacity: isLink ? 1 : 0,
        backgroundColor: '#FFF',
        boxShadow: '0 5px 10px rgba(0, 0, 0, 0.3)',
        borderRadius: 1,
        transition: 'opacity 0.3s ease-in-out',
        pointerEvents: isLink ? 'auto' : 'none',
      }}
    >
      {isLink &&
        (isLinkEditMode ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto auto',
              gap: 2,
              alignItems: 'center',
              padding: 1,
              width: '100%',
              height: 80,
            }}
          >
            <TextField
              inputRef={urlInputRef}
              id='link-url'
              label='Link URL'
              variant='outlined'
              size='small'
              fullWidth
              value={editedLinkUrl}
              onChange={(e) => onEditedLinkUrlChange(e.target.value)}
            />
            <TextField
              inputRef={textInputRef}
              id='link-text'
              label='Display Text'
              variant='outlined'
              size='small'
              fullWidth
              value={editedLinkText}
              onChange={(e) => onEditedLinkTextChange(e.target.value)}
            />
            <IconButton
              onMouseDown={(e) => e.preventDefault()}
              onClick={onSubmit}
              color='success'
              aria-label='Submit link changes'
            >
              <SubmitIcon />
            </IconButton>
            <IconButton
              onMouseDown={(e) => e.preventDefault()}
              onClick={onCancel}
              color='error'
              aria-label='Cancel link changes'
            >
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              gap: 2,
              alignItems: 'center',
              padding: 1,
              width: '100%',
              height: 60,
            }}
          >
            <a
              href={sanitizeUrl(linkUrl)}
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none' }}
            >
              {linkUrl}
            </a>
            <IconButton
              onMouseDown={(e) => e.preventDefault()}
              onClick={onEdit}
              aria-label='Edit link'
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onMouseDown={(e) => e.preventDefault()}
              onClick={onRemove}
              aria-label='Remove link'
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        ))}
    </Box>
  );
}
